import {
  CARD_CONDITIONS,
  PRODUCT_CATEGORIES,
  PRODUCT_LANGUAGES,
  PRODUCT_SERIES,
  type CardCondition,
  type ProductCategory,
  type ProductLanguage,
  categoryLabel,
  conditionLabel,
  languageLabel,
} from '@akknerds/shared';
import { Button, Label, Separator, cn } from '@akknerds/ui';
import { RotateCcw } from 'lucide-react';
import { type ProductFilters, isDefaultFilters } from '../../lib/filters';

interface ProductFiltersPanelProps {
  value: ProductFilters;
  onChange: (patch: Partial<ProductFilters>) => void;
  onReset: () => void;
}

interface ChipOption<T extends string> {
  value: T;
  label: string;
}

function FilterChips<T extends string>({
  legend,
  value,
  options,
  onSelect,
}: {
  legend: string;
  value: T;
  options: ChipOption<T>[];
  onSelect: (value: T) => void;
}) {
  const legendId = `filter-${legend.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div className="flex flex-col gap-2" role="group" aria-labelledby={legendId}>
      <p id={legendId} className="text-foreground text-sm font-semibold">
        {legend}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value || 'all'}
              type="button"
              onClick={() => onSelect(option.value)}
              aria-pressed={active}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                active
                  ? 'bg-secondary text-foreground'
                  : 'bg-secondary/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CATEGORY_OPTIONS: { value: ProductCategory | 'all'; label: string }[] = [
  { value: 'all', label: 'All products' },
  ...PRODUCT_CATEGORIES.map((value) => ({ value, label: categoryLabel(value) })),
];

const LANGUAGE_OPTIONS: ChipOption<ProductLanguage | ''>[] = [
  { value: '', label: 'All' },
  ...PRODUCT_LANGUAGES.map((language) => ({
    value: language,
    label: languageLabel(language),
  })),
];

const CONDITION_OPTIONS: ChipOption<CardCondition | ''>[] = [
  { value: '', label: 'All' },
  ...CARD_CONDITIONS.map((condition) => ({
    value: condition,
    label: conditionLabel(condition),
  })),
];

/** Shop sidebar filters — category/series/language/condition are static domain enums (no API wait). */
export function ProductFiltersPanel({ value, onChange, onReset }: ProductFiltersPanelProps) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <div className="flex flex-col gap-2" role="group" aria-labelledby="filter-category">
        <p id="filter-category" className="text-foreground text-sm font-semibold">
          Category
        </p>
        <div className="flex flex-col gap-1">
          {CATEGORY_OPTIONS.map((category) => {
            const active = value.category === category.value;
            return (
              <button
                key={category.value}
                type="button"
                onClick={() => onChange({ category: category.value })}
                aria-pressed={active}
                className={cn(
                  'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  active
                    ? 'bg-secondary text-foreground font-semibold'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                )}
              >
                {category.label}
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-2">
        <Label htmlFor="series-filter">Series</Label>
        <select
          id="series-filter"
          value={value.series}
          onChange={(e) => onChange({ series: e.target.value })}
          className="border-input bg-background text-foreground focus-visible:border-ring focus-visible:ring-ring/40 h-11 w-full appearance-none rounded-lg border px-3 text-sm focus-visible:outline-none focus-visible:ring-2"
        >
          <option value="">All series</option>
          {PRODUCT_SERIES.map((series) => (
            <option key={series} value={series}>
              {series}
            </option>
          ))}
        </select>
      </div>

      <Separator />

      <FilterChips
        legend="Language"
        value={value.language}
        options={LANGUAGE_OPTIONS}
        onSelect={(language) => onChange({ language })}
      />

      <FilterChips
        legend="Condition"
        value={value.condition}
        options={CONDITION_OPTIONS}
        onSelect={(condition) => onChange({ condition })}
      />

      {!isDefaultFilters(value) && (
        <Button variant="ghost" size="sm" onClick={onReset} className="self-start">
          <RotateCcw /> Reset filters
        </Button>
      )}
    </div>
  );
}
